import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useAuth from '../Hooks/useAuth';
import { addPackage, updatePackage, removePackage, removeProductType } from '../../state/slices/PackageSlice';
import { addDeletedPackage, removeDeletedPackage } from '../../state/slices/DeletedPackageSlice';
import { NotifyInfo } from '../Notify/notify';

// REACT_APP_API_BASE_URL ends in /api; SignalR hubs are mapped at the root level
const HUB_URL = process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '') + '/hubs/packages';

// Pages that rely on real-time package updates
const PACKAGE_PAGES = ['/tork', '/map', '/markers'];

const usePackageHub = () => {
    const { jwtToken, email: currentUserEmail } = useAuth();
    const dispatch = useDispatch();
    const connectionRef = useRef(null);
    const location = useLocation();

    const notify = (msg) => {
        if (location.pathname === '/tork') NotifyInfo(msg);
    };

    useEffect(() => {
        if (!jwtToken) return;
        if (!PACKAGE_PAGES.includes(location.pathname)) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () => jwtToken
            })
            .withAutomaticReconnect()
            .build();

        const isOtherUser = (triggeredByEmail) => triggeredByEmail !== currentUserEmail;

        connection.on('PackageAdded', ({ package: pkg, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(addPackage(pkg));
                notify(`[${triggeredByEmail}] - La ${pkg.name} på ${pkg.fotName}`);
            }
        });

        connection.on('PackageUpdated', ({ package: pkg, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(updatePackage(pkg));
                notify(`[${triggeredByEmail}] - Uppdaterade ${pkg.name} på ${pkg.fotName}`);
            }
        });

        connection.on('PackageRemoved', ({ packageId, package: pkg, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(removePackage({ id: packageId }));
                if (pkg) dispatch(addDeletedPackage(pkg));
                const label = pkg ? `${pkg.name} – ${pkg.fotName}` : packageId;
                notify(`[${triggeredByEmail}] - Hanterade ${label}`);
            }
        });

        connection.on('PackageRestored', ({ package: pkg, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(removeDeletedPackage(pkg));
                dispatch(addPackage(pkg));
                notify(`[${triggeredByEmail}] - Återställde ${pkg.name} – ${pkg.fotName}`);
            }
        });

        connection.on('ProductTypeAdded', ({ package: pkg, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(addPackage(pkg));
                notify(`[${triggeredByEmail}] - Lade till en produkttyp`);
            }
        });

        connection.on('ProductTypeRemoved', ({ name, triggeredByEmail }) => {
            if (isOtherUser(triggeredByEmail)) {
                dispatch(removeProductType(name));
                notify(`[${triggeredByEmail}] - Tog bort en produkttyp`);
            }
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        connectionRef.current = connection;

        return () => {
            connection.stop();
        };
    // Re-run when navigating to/from a relevant page or when jwtToken refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwtToken, location.pathname]);

    return null;
};

export default usePackageHub;
